// A player's on account — what they owe the organisation (F-POS-07, spec/06 §8.6) — as TanStack
// Query hooks over the generated client, `packages/contracts/src/routes/credit.ts`:
//
//   GET  …/venues/{v}/players/{p}/credit                    balance, limit, available   orders:read
//   PUT  …/venues/{v}/players/{p}/credit                    set the player's limit      credit:manage
//   POST …/venues/{v}/players/{p}/receivable/write-off      forgive what is owed        credit:write_off
//   POST …/venues/{v}/players/{p}/receivable/settle         collect it by a tender      orders:create
//
// The balance is organisation-wide (one per player across the organisation's venues), but every
// route is read and written at a venue: the venue decides the currency, the effective limit and
// the drawer a collection lands in. So these hooks need the current venue; the organisation console
// has none and does not show the section. Every write answers the player's new position, which
// replaces the cached one.

import type { Receivable, SetPlayerCreditRequest, SettleReceivableRequest, WriteOffReceivableRequest } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

export const creditKeys = {
  position: (organizationId: string, venueId: string, playerId: string) => ['players', organizationId, 'credit', venueId, playerId] as const,
};

/** The player's on-account position at this venue's organisation. `enabled` false skips the read. */
export function usePlayerCredit(organizationId: string, venueId: string | undefined, playerId: string, enabled = true) {
  const api = useApi();
  return useQuery<Receivable>({
    queryKey: creditKeys.position(organizationId, venueId ?? '', playerId),
    enabled: enabled && venueId !== undefined,
    queryFn: () => api.getPlayerCredit({ path: { organizationId, venueId: venueId ?? '', playerId } }),
  });
}

/** Write the new position into the cache, so the figures change the moment the server answers. */
function useStorePosition(organizationId: string, venueId: string, playerId: string) {
  const queryClient = useQueryClient();
  return (position: Receivable) => {
    queryClient.setQueryData(creditKeys.position(organizationId, venueId, playerId), position);
    void queryClient.invalidateQueries({ queryKey: creditKeys.position(organizationId, venueId, playerId) });
  };
}

/** Collect money owed by a counter tender (cash or counter UPI) into this venue's drawer. Audited. */
export function useSettleReceivable(organizationId: string, venueId: string, playerId: string) {
  const api = useApi();
  const store = useStorePosition(organizationId, venueId, playerId);
  return useMutation({
    mutationFn: (body: SettleReceivableRequest) => api.settleReceivable({ path: { organizationId, venueId, playerId }, body }),
    onSuccess: store,
  });
}

/** Forgive an amount owed, with a reason (`credit:write_off`). Audited. */
export function useWriteOffReceivable(organizationId: string, venueId: string, playerId: string) {
  const api = useApi();
  const store = useStorePosition(organizationId, venueId, playerId);
  return useMutation({
    mutationFn: (body: WriteOffReceivableRequest) => api.writeOffReceivable({ path: { organizationId, venueId, playerId }, body }),
    onSuccess: store,
  });
}

/** Set the player's own credit limit (`credit:manage`). Zero turns on account off for them. Audited. */
export function useSetPlayerCredit(organizationId: string, venueId: string, playerId: string) {
  const api = useApi();
  const store = useStorePosition(organizationId, venueId, playerId);
  return useMutation({
    mutationFn: (body: SetPlayerCreditRequest) => api.setPlayerCredit({ path: { organizationId, venueId, playerId }, body }),
    onSuccess: store,
  });
}
